import React from 'react'
import { camelCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Callout,
  CopyButton,
  Highlight,
  PagingSidePanelType,
  SidePanel,
  Tabs,
} from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { useCustomerApplicationQuery } from 'hooks/queries/Customers/useCustomerApplicationQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { Application } from 'types/customers'
import { highlightRowBasedOnId } from 'utils/table/table'
import {
  DOCUMENT_TYPES,
  REQUIRED_ACTION_CATEGORY_ASSOCIATES,
  REQUIRED_ACTION_CATEGORY_DOCUMENT_REQUIREMENTS,
} from '../helpers/constants'
import { statusHighlightTypeMap, statusLabelMap } from '../utils/utils'
import { CustomerAssociatesTab } from './CustomerAssociatesTab'
import { CustomerDocumentsTab } from './CustomerDocumentsTab'
import { CustomerSidePanelBody } from './CustomerSidePanelBody'

export interface ApplicationSidePanelProps {
  isOpen: boolean
  application: Application | undefined
  paging: PagingSidePanelType
  onClose: () => void
}

export const CustomerSidePanel = ({
  isOpen,
  application,
  paging,
  onClose,
}: ApplicationSidePanelProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)

  const onboardingId = application?.id || ''

  const { data, isLoading, isError, refetch } = useCustomerApplicationQuery(onboardingId)

  const onboardingLink = application?.authenticatedLink?.link

  const { reference, id, status } = application || {}
  const companyName = data?.company?.name || application?.name || reference || id

  const pendingDocumentCodes = new Set(
    data?.requiredActions
      ?.filter(action => action?.category === REQUIRED_ACTION_CATEGORY_DOCUMENT_REQUIREMENTS)
      .map(action => camelCase(action?.code)) ?? []
  )
  const hasOutstandingDocuments = DOCUMENT_TYPES.some(docKey => pendingDocumentCodes.has(docKey))

  const requiresAssociateData =
    data?.requiredActions?.some(
      action => action?.category === REQUIRED_ACTION_CATEGORY_ASSOCIATES
    ) ?? false

  return (
    <SidePanel
      id="side-panel-application"
      isOpen={isOpen}
      paging={paging}
      onClose={onClose}
      activeRowId={application?.id}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        <Box flexCol gap={16}>
          <SidePanelHeaderTemplateTwo
            heading={{
              iconUrls: [],
              primaryLabel: <h2 className="text-foreground-prominent text-h2">{companyName}</h2>,
              secondaryLabel: status && (
                <Highlight type={statusHighlightTypeMap[status]}>
                  {statusLabelMap[status] ?? status}
                </Highlight>
              ),
            }}
          />
          {onboardingLink && (
            <SidePanelSection>
              <Callout
                GhostButton={
                  <CopyButton copyText={onboardingLink} testId="copy-onboarding-link">
                    {t('customers.onboardingLink')}
                  </CopyButton>
                }
              >
                {t('customers.onboardingLinkHelp')}
              </Callout>
            </SidePanelSection>
          )}
        </Box>
      }
      body={
        <Box tag="section" width="full" background="standard">
          <Tabs
            tabs={[
              {
                id: 'overview',
                header: t('embeddedCustomers.tabs.overview'),
                content: (
                  <CustomerSidePanelBody
                    application={application}
                    data={data}
                    isLoading={isLoading}
                    isError={isError}
                    refetch={refetch}
                  />
                ),
              },
              ...(data && onboardingLink
                ? [
                    {
                      id: 'documents',
                      header: t('embeddedCustomers.tabs.documents'),
                      content: (
                        <CustomerDocumentsTab
                          customerReference={id ?? ''}
                          pendingDocumentCodes={pendingDocumentCodes}
                        />
                      ),
                      badgeCount: hasOutstandingDocuments ? '!' : undefined,
                    },
                    {
                      id: 'associates',
                      header: t('embeddedCustomers.tabs.associates'),
                      content: <CustomerAssociatesTab associates={data} />,
                      badgeCount: requiresAssociateData ? '!' : undefined,
                    },
                  ]
                : []),
            ]}
            defaultTabId="overview"
          />
        </Box>
      }
    />
  )
}
